import { EXPERIMENT_STEPS, describeDeltaE, labToSrgb } from '@dyeatlas/shared'
import { useQuery } from '@tanstack/react-query'
import { BookMarked, Folder, FlaskConical, FolderPlus, Link2, Pencil } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { STEP_FIELDS } from './wizard/fields'
import { hasValue, labOf, resolve, type DetailRecord } from './recordFields'
import { SaveToWorkspaceDialog } from '../workspaces/dialogs'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { LoadingPanel } from '@/components/ui/misc'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { api } from '@/lib/api'
import { cn, formatDate } from '@/lib/utils'


export function RecordDetailModal({
  recordId,
  onOpenChange,
  onOpenRecord,
  allowSaveToProject = true,
}: {
  recordId: string | null
  onOpenChange: (open: boolean) => void
  onOpenRecord?: (id: string) => void
  allowSaveToProject?: boolean
}) {
  const [saveOpen, setSaveOpen] = useState(false)
  const { user, isCurator } = useCurrentUser()

  const { data, isLoading } = useQuery({
    queryKey: ['browse', 'record', recordId],
    queryFn: () => api.get<{ record: DetailRecord }>(`/browse/${recordId}`),
    enabled: Boolean(recordId),
  })

  const record = data?.record
  // Records moved into a project are no longer listed in My Workspace, so their
  // author edits them from here.
  const canEditOwn =
    record !== undefined &&
    !record.sourceProjectId &&
    record.ownerId === user?.id &&
    ['draft', 'finished', 'rejected'].includes(record.status as string)
  const lab = record ? labOf(record) : null
  const swatch = lab ? labToSrgb(lab) : null

  return (
    <>
    <SaveToWorkspaceDialog recordId={recordId} open={saveOpen} onOpenChange={setSaveOpen} />

    <Dialog open={Boolean(recordId)} onOpenChange={onOpenChange} size="lg" title={record?.title}>
      {isLoading || !record ? (
        <LoadingPanel label="Loading record" />
      ) : (
        <div className="space-y-6">
          <header className="flex items-start gap-4">
            <span
              className="size-20 shrink-0 rounded-[var(--radius-control)] border border-line"
              style={{ backgroundColor: record.swatchHex ?? 'var(--color-paper-muted)' }}
            />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wide text-ink-faint">
                {record.kind === 'reference' ? (
                  <BookMarked className="size-3.5" />
                ) : (
                  <FlaskConical className="size-3.5" />
                )}
                {record.code}
              </p>
              <p className="mt-1 text-sm text-ink-soft">
                {record.dyeSource}
                {record.dyeSourceScientific && (
                  <span className="ml-1 italic text-ink-muted">{record.dyeSourceScientific}</span>
                )}
              </p>
              <p className="mt-0.5 text-[13px] text-ink-muted">
                {record.researcherName}
                {record.publishedAt && ` · published ${formatDate(record.publishedAt)}`}
              </p>
              {record.kind === 'experiment' && record.projectName && (
                <p className="mt-1 flex items-center gap-1 text-[13px] text-ink-soft">
                  <Folder className="size-3.5 text-ink-faint" />
                  Project: <span className="font-semibold text-ink">{record.projectName}</span>
                </p>
              )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              {swatch && (
                <div className="rounded-[var(--radius-control)] border border-line bg-paper-muted px-3 py-2 text-right">
                  <p className="font-mono text-[13px] font-semibold text-ink">
                    L* {lab!.L} a* {lab!.a} b* {lab!.b}
                  </p>
                  <p className="mt-0.5 font-mono text-[10px] text-ink-muted">
                    {swatch.hex}
                    {swatch.outOfGamut && <span className="text-caution"> (outside sRGB)</span>}
                  </p>
                </div>
              )}
              {canEditOwn && (
                <Link to={`/database/${record.id}/edit`}>
                  <Button variant="secondary" size="sm">
                    <Pencil className="size-4" />
                    Edit
                  </Button>
                </Link>
              )}
              {record.sourceProjectId ? (
                isCurator && (
                  <Link to={`/database/${record.id}/edit`}>
                    <Button variant="secondary" size="sm">
                      <Pencil className="size-4" />
                      Edit public record
                    </Button>
                  </Link>
                )
              ) : (
                allowSaveToProject && record.kind === 'experiment' && (
                  <Button variant="secondary" size="sm" onClick={() => setSaveOpen(true)}>
                    <FolderPlus className="size-4" />
                    Save to project
                  </Button>
                )
              )}
            </div>
          </header>

          {record.photos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {record.photos.map((photo) => (
                <img
                  key={photo.id}
                  src={photo.url}
                  alt={photo.filename}
                  className="size-28 rounded-[var(--radius-control)] border border-line object-cover"
                />
              ))}
            </div>
          )}

          {record.notes && (
            <p className="rounded-[var(--radius-control)] border border-line bg-paper-muted p-3 text-[13px] leading-relaxed text-ink-soft">
              {record.notes}
            </p>
          )}

          {record.kind === 'reference' ? (
            <ReferenceDetails record={record} />
          ) : (
            <ExperimentDetails record={record} />
          )}

          {record.linkedRecords.length > 0 && (
            <section>
              <h3 className="mb-2 flex items-center gap-1.5 font-display text-base font-semibold text-ink">
                <Link2 className="size-4 text-ink-faint" />
                {record.kind === 'reference' ? 'Experiments reproducing this recipe' : 'Source reference'}
              </h3>
              <ul className="space-y-1">
                {record.linkedRecords.map((link) => (
                  <li key={link.id}>
                    <button
                      type="button"
                      onClick={() => onOpenRecord?.(link.id)}
                      className="flex w-full items-center gap-2 rounded-[var(--radius-control)] border border-line px-3 py-2 text-left text-[13px] transition-colors hover:border-brand-line hover:bg-brand-soft"
                    >
                      <Badge tone="muted">{link.code}</Badge>
                      <span className="truncate text-ink">{link.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {swatch && (
            <p className="text-[12px] text-ink-faint">
              Colour differences are reported as ΔE₀₀ (CIEDE2000); {describeDeltaE(1).toLowerCase()}{' '}
              at ΔE₀₀ 1.
            </p>
          )}
        </div>
      )}
    </Dialog>
    </>
  )
}

/** Experiments read back through the same step definitions the wizard uses. */
function ExperimentDetails({ record }: { record: DetailRecord }) {
  return (
    <>
      {EXPERIMENT_STEPS.filter((step) => step.schema !== null).map((step) => {
        const fields = STEP_FIELDS[step.key as keyof typeof STEP_FIELDS].filter((field) =>
          hasValue(resolve(field, record)),
        )
        if (fields.length === 0) return null

        return (
          <section key={step.key}>
            <h3 className="mb-2 font-display text-base font-semibold text-ink">{step.title}</h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-[var(--radius-control)] border border-line p-4 text-[13px]">
              {fields.map((field) => (
                <div
                  key={field.name}
                  className={cn('flex justify-between gap-4', field.wide && 'col-span-2')}
                >
                  <dt className="text-ink-muted">{field.label}</dt>
                  <dd className="text-right text-ink">{resolve(field, record)}</dd>
                </div>
              ))}
            </dl>
          </section>
        )
      })}
    </>
  )
}

function ReferenceDetails({ record }: { record: DetailRecord }) {
  const rows: [string, string | number | null][] = [
    ['Manuscript', record.manuscript],
    ['Recipe number', record.recipeNumber],
    ['Year', record.referenceYear],
    ['Fiber', record.vocabulary[record.texFiberId as string] ?? null],
    ['Dye sources', record.dyeSource],
    ['Mordants', record.mordants.map((mordant) => mordant.name).join(', ') || null],
    ['Mordant composition', (record.morMordantNotes as string | null) ?? null],
    ['Transcribed by', record.researcherName],
  ]

  return (
    <section>
      <h3 className="mb-2 font-display text-base font-semibold text-ink">Source</h3>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-[var(--radius-control)] border border-line p-4 text-[13px]">
        {rows
          .filter(([, value]) => hasValue(value))
          .map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4">
              <dt className="text-ink-muted">{label}</dt>
              <dd className="text-right text-ink">{value}</dd>
            </div>
          ))}
      </dl>
    </section>
  )
}
